/**
 * aggregation-task.component.ts
 * Copyright © 2016-2018, My Coverage Plan, Inc.
 * All rights reserved.
 */

/**
 * @author Taylor DH Smith <tdsmith@safetyweb.org>
 */

import { Component, Input } from "@angular/core";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { Modal } from "angular2-modal/plugins/bootstrap";

import { UserService } from "../auth/identity/user.service";
import { SubjectService } from "../subjects/subject.service";
import { ModalHelpers } from "../support/utilities";

import { Aggregation } from "./aggregation";
import { AggregationService } from "./aggregation.service";
import { Aggregator } from "./aggregator";
import { ToolboxTaskComponent } from "./toolbox-task.component";
import { ModalService } from "app/common/modal.service";

/**
 * The {@link AggregationTaskComponent} holds all content for IO related to one
 * aggregation "task" (an abstraction associated with one runnable server agent)
 * including its configuration, progress/status, and final output.
 */
@Component({
    selector: "aggregation-task",
    template: `
<div
    #panel
    class="panel panel-default">
    <div class="panel-heading" (click)="panel.closed = !panel.closed">
        <h3 class="panel-title">
            <span
                class="glyphicon"
                [class.glyphicon-expand]="panel.closed"
                [class.glyphicon-collapse-down]="!panel.closed">
            </span>
            <a
                title="Rename this query..."
                (click)="$event.stopPropagation(); renameTask()">
                {{task.title}}</a>
            <!--<small>({{task.readableState}})</small>-->
            <small>
                (algorithm:
                <span class="text-success">{{task.algorithm}}</span>,
                group:
                <span class="text-info">{{task.group}}</span>)
            </small>
            <button
                type="button"
                class="btn btn-xs btn-danger pull-right"
                (click)="release()">
                Remove
            </button>
        </h3>
    </div>
    <div class="panel-body collapse" [class.in]="!panel.closed">
        <h4>Options</h4>
        <div class="row form-group">
            <div class="col-sm-4">
                <label>Algorithm:</label>
                <div class="form-control" disabled>
                    {{task.algorithm}}
                </div>
            </div>
            <div class="col-sm-4">
                <label>Group:</label>
                <div class="form-control" disabled>
                    {{task.group}}
                </div>
            </div>
            <div
                *ngIf="task.targetVariableOptions.length > 0"
                class="col-sm-4">
                <label>Target State Variable</label>
                <select
                    class="form-control"
                    [formControl]="task.targetSelector">
                    <option
                        *ngFor="let variable of task.targetVariableOptions"
                        [value]="variable.name">
                        {{variable.displayName}}
                    </option>
                </select>
            </div>
        </div>
        <div
            *ngIf="!(task.isStarted && task.filter === null)"
            class="form-group">
            <label>
                Filter
            </label>
            <span class="btn-group">
                <button
                    *ngIf="!task.isStarted"
                    type="button"
                    class="btn btn-xs btn-default"
                    (click)="resetFilter(true)"
                    [disabled]="task.isStarted">
                    {{task.filter === null ? "Add (Simple)" : "Reset (Simple)"}}
                </button>
                <button
                    *ngIf="!task.isStarted"
                    type="button"
                    class="btn btn-xs btn-info"
                    (click)="resetFilter()"
                    [disabled]="task.isStarted">
                    {{task.filter === null ? "Add (Advanced)" : "Reset (Advanced)"}}
                </button>
                <button
                    *ngIf="!task.isStarted && task.filter !== null"
                    type="button"
                    class="btn btn-xs btn-danger"
                    (click)="removeFilter()"
                    [disabled]="task.isStarted">
                    Remove
                </button>
            </span>
            <filter-node
                *ngIf="task.filter !== null && !task.isStarted && !simpleFilter"
                [node]="task.filter"
                [variables]="task.filterOptions"
                [root]="true">
            </filter-node>
            <simple-filter-node
                *ngIf="task.filter !== null && !task.isStarted && simpleFilter"
                [node]="task.filter"
                [variables]="task.filterOptions">
            </simple-filter-node>
            <div
                style="margin-left: 2em"
                *ngIf="task.filterString"
                [innerHtml]="task.filterString">
            </div>
        </div>
        <div class="row">
            <div class="col-md-3">
                <button
                    class="btn btn-success p100"
                    (click)="run()"
                    [disabled]="task.isStarted && !task.isComplete">
                    <span
                        class="glyphicon"
                        [class.glyphicon-play]="!task.isComplete"
                        [class.glyphicon-repeat]="task.isComplete">
                    </span>
                    {{ task.isComplete ? "Re-run" : "Run" }}
                </button>
            </div>
            <div class="col-md-3">
                <button
                    *ngIf="!task.isPaused"
                    class="btn btn-info p100"
                    (click)="pause(true)"
                    [disabled]="!task.isRunning">
                    <span class="glyphicon glyphicon-pause"></span>
                    Pause
                </button>
                <button
                    *ngIf="task.isPaused"
                    class="btn btn-info p100"
                    (click)="pause(false)">
                    <span class="glyphicon glyphicon-step-forward"></span>
                    Resume
                </button>
            </div>
            <div class="col-md-3">
                <button
                    class="btn btn-danger p100"
                    (click)="cancel()"
                    [disabled]="!(task.isRunning || task.isPaused)">
                    <span class="glyphicon glyphicon-remove"></span>
                    Cancel
                </button>
            </div>
            <!--<button
                class="btn btn-default"
                (click)="getProgress()"
                [disabled]="task.isComplete || !task.isStarted">
                <span class="glyphicon glyphicon-tasks"></span>
                Request Progress
            </button>-->
            <div class="col-md-3">
                <button
                    class="btn btn-default p100"
                    (click)="saveSchema()"
                    [disabled]="!task.isComplete">
                    <span class="glyphicon glyphicon-pushpin"></span>
                    Save as Template
                </button>
            </div>
        </div>
    </div>

    <div
        *ngIf="task.isStarted"
        class="panel-body collapse"
        [class.in]="!panel.closed">
        <task-progress [task]="task"></task-progress>
    </div>

    <div
        *ngIf="task.isComplete"
        class="panel-body collapse reports"
        [class.in]="!panel.closed">
        <h3>Results</h3>

        <!-- no results sent -->
        <h4 *ngIf="task.noResults" class="text-center text-danger">
            No Results
        </h4>

        <!-- "standard" (valued) results -->
        <table
            *ngIf="task.standardResults.length"
            class="table"
            [class.single-result]="task.standardResults.length === 1">
            <thead>
                <th *ngFor="let result of task.standardResults">
                    {{result.displayName}}
                </th>
            </thead>
            <tbody><tr>
                <td *ngFor="let result of task.standardResults">
                    <typed-display [input]="result"></typed-display>
                </td>
            </tr></tbody>
        </table>

        <!-- frequency results -->
        <div
            *ngFor="let freq of task.frequencyResults"
            class="row frequency-report">
        <div class="col-md-4">
            <table class="table">
                <thead>
                    <tr>
                        <th
                            [sorts]="freq.value.frequencies"
                            dot="element"
                            [asType]="freq.value.type"
                            [after]="chart"
                            [autorun]="true">
                            Element
                        </th>
                        <th
                            [sorts]="freq.value.frequencies"
                            dot="frequency"
                            asType="natural number"
                            [after]="chart">
                            Frequency
                        </th>
                    </tr>
                </thead>
                <tr *ngFor="let entry of (freq.value.frequencies | slice:0:12)">
                    <td>
                        <typed-display
                            [value]="entry.element"
                            [wrapper]="{type: freq.value.type, cardinality: freq.value.cardinality}">
                        </typed-display>
                    </td>
                    <td>
                        {{entry.frequency}}
                    </td>
                </tr>
                <tr *ngIf="freq.value.frequencies.length > 12">
                    <td colspan="2" class="text-warning">
                        <em>
                            Truncated
                            {{freq.value.frequencies.length - 20}}
                            entries...
                        </em>
                    </td>
                </tr>
            </table>
        </div>
        <div class="col-md-8">
            <frequency-chart
                #chart
                [data]="freq.value">
            </frequency-chart>
        </div>
        </div>

        <!-- context results -->
        <div
            *ngFor="let result of task.contextResults; let r = index"
            class="context-result">
            <h4>{{result.displayName}}</h4>
            <table class="table">
                <thead>
                    <th
                        *ngFor="let column of result.columns; let i = index">
                        {{column}}
                    </th>
                    <th>
                        Actions
                    </th>
                </thead>
                <tbody *ngIf="result.cardinality === 1">
                    <tr>
                        <td *ngFor="let data of result.value.summary">
                            <typed-display [input]="data">
                            </typed-display>
                        </td>
                        <td>
                            <a
                                type="button"
                                class="btn btn-success btn-xs"
                                (click)="openResultSubject(r, result.itemId)">
                                Open
                            </a>
                        </td>
                    </tr>
                </tbody>
                <tbody *ngIf="result.cardinality !== 1">
                    <tr *ngFor="let context of result.value">
                        <td *ngFor="let data of context.summary">
                            <typed-display [input]="data">
                            </typed-display>
                        </td>
                        <td>
<!-- These are disabled until we revisit higher cardinality context results -->
                            <button
                                type="button"
                                class="btn btn-success btn-xs"
                                disabled
                                title="Higher cardinality open is unavailable">
                                Open
                            </button>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
    </div>
</div>
    `,
    styles: [
        `
        .single-result th, .single-result td {
            font-size: 1.6em;
        }
        .panel-body + .panel-body {
            border-top: 1px solid #ddd;
        }
        .panel h3, .panel h4 {
            margin-top: 0;
        }
        .frequency-report table {
            padding: 10px;
            margin: auto;
        }
        .reports td, .reports th {
            text-align: center;
        }
        .context-result th, .context-result h4 {
            text-transform: capitalize;
        }
        /*
        .progress-bar {
            min-width: 70px;
        }
        .progress-bar + .progress-bar {
            max-width: calc(100% - 70px);
        } */
        `
    ]
})
export class AggregationTaskComponent extends ToolboxTaskComponent
{
    @Input()
    public task : Aggregation;

    constructor (
        public service : AggregationService,
        public modals : ModalService,
        public subjects : SubjectService
    ) {
        super(service, modals);
    }

    /**
     * Attempt to run the aggregation.
     */
    public run () : void
    {
        if (this.task.requiresStateVariable
            && this.task.targetSelector.invalid)
        {
            // The user has not selected a state variable when one was required
            return alert("You must select a target state variable!");
        }
        if (this.task.isComplete)
        {
            // If the user clicks run again after it has previously been deemed
            // complete, we must erase the past progress data.
            this.task.clearProgress();
        }
        this.service.run(this.task);
    }

    public openResultSubject (r : number, i : number) : void
    {
        this.subjects.openReference(this.task.buildReportRef(r, i)).subscribe();
    }
}



// WEBPACK FOOTER //
// ./~/angular-router-loader/src!./app/aggregation/aggregation-task.component.ts